import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

// ─── Cart Context ─────────────────────────────────────────────────────────────
const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ad_cart')) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('ad_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i._id === product._id);

      if (existing) {
        return prev.map((i) =>
          i._id === product._id ? { ...i, qty: i.qty + qty } : i
        );
      }

      return [...prev, { ...product, qty }];
    });

    toast.success(`${product.name} added to cart`);
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart((prev) => prev.filter((i) => i._id !== id));
  }, []);

  const updateQty = useCallback(
    (id, qty) => {
      if (qty < 1) {
        removeFromCart(id);
        return;
      }

      setCart((prev) =>
        prev.map((i) => (i._id === id ? { ...i, qty } : i))
      );
    },
    [removeFromCart]
  );

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartSubtotal = cart.reduce(
    (s, i) => s + (i.discountPrice || i.price) * i.qty,
    0
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQty,
        clearCart,
        cartCount,
        cartSubtotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);

// ─── Wishlist Context ─────────────────────────────────────────────────────────
const WishlistContext = createContext();

export function WishlistProvider({ children }) {
  const [wishlist, setWishlist] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ad_wishlist')) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('ad_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  const isWished = useCallback(
    (id) => wishlist.some((item) => item._id === id),
    [wishlist]
  );

  const addToWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.some((item) => item._id === product._id);

      if (exists) return prev;

      toast.success(`${product.name} added to wishlist`);
      return [...prev, product];
    });
  }, []);

  const removeFromWishlist = useCallback((id) => {
    setWishlist((prev) => prev.filter((item) => item._id !== id));
    toast.success('Removed from wishlist');
  }, []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.some((item) => item._id === product._id);

      if (exists) {
        toast.success('Removed from wishlist');
        return prev.filter((item) => item._id !== product._id);
      }

      toast.success(`${product.name} added to wishlist`);
      return [...prev, product];
    });
  }, []);

  const clearWishlist = useCallback(() => {
    setWishlist([]);
    toast.success('Wishlist cleared');
  }, []);

  const wishlistCount = wishlist.length;

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        wishlistCount,
        isWished,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        clearWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);

// ─── Auth Context ─────────────────────────────────────────────────────────────
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ad_user'));
    } catch {
      return null;
    }
  });

  const [admin, setAdmin] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ad_admin'));
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => localStorage.getItem('ad_token') || '');

  const loginUser = useCallback((userData, tokenStr) => {
    setUser(userData);
    setToken(tokenStr);
    localStorage.setItem('ad_user', JSON.stringify(userData));
    localStorage.setItem('ad_token', tokenStr);
  }, []);

  const loginAdmin = useCallback((adminData, tokenStr) => {
    setAdmin(adminData);
    setToken(tokenStr);
    localStorage.setItem('ad_admin', JSON.stringify(adminData));
    localStorage.setItem('ad_token', tokenStr);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setAdmin(null);
    setToken('');

    localStorage.removeItem('ad_user');
    localStorage.removeItem('ad_admin');
    localStorage.removeItem('ad_token');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        admin,
        token,
        loginUser,
        loginAdmin,
        logout,
        isAdmin: !!admin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);